/**
 * Neurula Report Builder
 * 
 * Interactive component for building and configuring reports with:
 * - Drag and drop filter building
 * - Real-time preview
 * - Template selection
 * - Data source configuration
 * - Export options
 */

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { 
    Container, 
    Row, 
    Col, 
    Card, 
    Button, 
    Form, 
    Modal, 
    Dropdown,
    Badge,
    Alert,
    Spinner,
    Accordion,
    ListGroup,
    Tab,
    Tabs
} from 'react-bootstrap';
import { 
    BsPlus, 
    BsGear, 
    BsEye, 
    BsSave, 
    BsDownload, 
    BsTrash,
    BsFilter,
    BsBarChart,
    BsFileText,
    BsTable,
    BsPieChart,
    BsGraphUp,
    BsCalendar,
    BsSearch,
    BsXCircle,
    BsCheck
} from 'react-icons/bs';
import { toast } from 'react-hot-toast';

// Import shared components
import { 
    ErrorBoundary,
    ServiceStatus,
    ReportingFallback
} from './shared';

import {
    fetchTemplates,
    fetchFilters,
    createReport,
    generateReportFromTemplate,
    fetchReportTypes,
    selectTemplates,
    selectFilters,
    selectReportTypes,
    selectReportsLoading
} from '../../redux/slices/reportingSlice';

import reportingService from '../../services/reportingService';

import './reporting.css';

const ReportBuilder = () => {
    const dispatch = useDispatch();
    
    // Redux state
    const templates = useSelector(selectTemplates);
    const filters = useSelector(selectFilters);
    const reportTypes = useSelector(selectReportTypes);
    const loading = useSelector(selectReportsLoading);
    
    // Local state
    const [activeStep, setActiveStep] = useState(1);
    const [reportConfig, setReportConfig] = useState({
        name: '',
        type: 'standard',
        description: '',
        templateId: null,
        filters: [],
        dateRange: {
            start: '',
            end: ''
        },
        fields: [],
        groupBy: [],
        sortBy: [],
        format: 'table'
    });
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [templateSelected, setTemplateSelected] = useState(false);
    const [showPreview, setShowPreview] = useState(false);
    
    // EMR Integration state
    const [emrDepartments, setEmrDepartments] = useState([]);
    const [emrCategories, setEmrCategories] = useState([]);
    const [emrForms, setEmrForms] = useState([]);
    const [selectedDepartment, setSelectedDepartment] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [selectedForm, setSelectedForm] = useState(null);
    const [formFields, setFormFields] = useState([]);
    const [emrLoading, setEmrLoading] = useState(false);
    const [templateSource, setTemplateSource] = useState('static'); // 'static' or 'emr'
    const [emrSyncStatus, setEmrSyncStatus] = useState(null);
    const [previewData, setPreviewData] = useState(null);
    const [availableFields] = useState([
        { id: 'patient_name', label: 'Patient Name', type: 'text' },
        { id: 'patient_id', label: 'Patient ID', type: 'text' },
        { id: 'date_of_birth', label: 'Date of Birth', type: 'date' },
        { id: 'admission_date', label: 'Admission Date', type: 'date' },
        { id: 'discharge_date', label: 'Discharge Date', type: 'date' },
        { id: 'department', label: 'Department', type: 'text' },
        { id: 'diagnosis', label: 'Diagnosis', type: 'text' },
        { id: 'treatment', label: 'Treatment', type: 'text' },
        { id: 'doctor', label: 'Doctor', type: 'text' },
        { id: 'insurance', label: 'Insurance', type: 'text' }
    ]);

    // Filter creation state
    const [newFilter, setNewFilter] = useState({
        field: '',
        operator: 'eq',
        value: '',
        data_type: 'string'
    });

    // Available filter operators
    const filterOperators = [
        { value: 'eq', label: 'Equals' },
        { value: 'ne', label: 'Not Equals' },
        { value: 'gt', label: 'Greater Than' },
        { value: 'gte', label: 'Greater Than or Equal' },
        { value: 'lt', label: 'Less Than' },
        { value: 'lte', label: 'Less Than or Equal' },
        { value: 'contains', label: 'Contains' },
        { value: 'starts_with', label: 'Starts With' },
        { value: 'ends_with', label: 'Ends With' },
        { value: 'in', label: 'In List' }
    ];
    
    // Fetch initial data
    useEffect(() => {
        dispatch(fetchTemplates());
        dispatch(fetchFilters());
        dispatch(fetchReportTypes());
        
        // Load EMR data
        loadEMRData();
    }, [dispatch]);

    // Load EMR integration data
    const loadEMRData = async () => {
        try {
            setEmrLoading(true);
            
            // Check EMR sync status first
            const syncStatus = await reportingService.getEMRSyncStatus();
            if (syncStatus.success) {
                setEmrSyncStatus(syncStatus.data);
                
                if (syncStatus.data.status === 'connected') {
                    // Load departments
                    const departmentsResult = await reportingService.getEMRDepartments();
                    if (departmentsResult.success) {
                        setEmrDepartments(departmentsResult.data);
                    }
                    
                    // Load categories
                    const categoriesResult = await reportingService.getEMRCategories();
                    if (categoriesResult.success) {
                        setEmrCategories(categoriesResult.data);
                    }
                    
                    // Load forms
                    const formsResult = await reportingService.getEMRForms({ active_only: true });
                    if (formsResult.success) {
                        setEmrForms(formsResult.data);
                    }
                }
            }
        } catch (error) {
            toast.error('Failed to connect to EMR system');
        } finally {
            setEmrLoading(false);
        }
    };
    
    // Handle report configuration changes
    const updateReportConfig = (field, value) => {
        setReportConfig(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Handle EMR form selection
    const handleEMRFormSelect = async (form) => {
        try {
            setSelectedForm(form);
            setEmrLoading(true);
            
            // Get full form definition
            const formResult = await reportingService.getEMRFormById(form.form_id || form.id);
            if (formResult.success) {
                const formData = formResult.data;
                
                // Extract fields from form definition
                const fields = extractFieldsFromEMRForm(formData);
                setFormFields(fields);
                
                // Generate or get template for this form
                const templateResult = await reportingService.generateTemplateFromForm(form.form_id || form.id);
                if (templateResult.success) {
                    const generatedTemplate = templateResult.data;
                    
                    // Set up report config with EMR form data
                    setReportConfig(prev => ({
                        ...prev,
                        templateId: generatedTemplate.id,
                        name: `${formData.name} - Clinical Report`,
                        type: formData.category_id || 'clinical',
                        description: `Report generated from ${formData.name} form submissions`,
                        fields: fields
                    }));
                    
                    setSelectedTemplate(generatedTemplate);
                    setTemplateSelected(true);
                    setActiveStep(3); // Advance to data configuration step
                    
                    toast.success(`EMR form "${formData.name}" selected with ${fields.length} fields`);
                } else {
                    toast.error('Failed to generate template from EMR form');
                }
            }
        } catch (error) {
            toast.error('Failed to load EMR form data');
        } finally {
            setEmrLoading(false);
        }
    };

    // Extract fields from EMR form definition
    const extractFieldsFromEMRForm = (formData) => {
        const fields = [];
        
        if (formData.fields && Array.isArray(formData.fields)) {
            formData.fields.forEach(field => {
                // Convert EMR field to report field format
                const reportField = {
                    id: field.name || field.field_name || field.id,
                    label: field.label || field.title || field.name,
                    type: mapEMRFieldType(field.type || field.field_type),
                    category: formData.category_id || 'clinical',
                    required: field.required || false,
                    options: field.options || []
                };
                
                // Add clinical relevance information
                if (field.clinical_relevance) {
                    reportField.clinical_relevance = field.clinical_relevance;
                }
                
                fields.push(reportField);
            });
        }
        
        // Add common clinical context fields
        const contextFields = [
            { id: 'patient_id', label: 'Patient ID', type: 'text', category: 'context' },
            { id: 'encounter_id', label: 'Encounter ID', type: 'text', category: 'context' },
            { id: 'submission_date', label: 'Submission Date', type: 'date', category: 'context' },
            { id: 'submitted_by', label: 'Submitted By', type: 'text', category: 'context' }
        ];
        
        contextFields.forEach(contextField => {
            if (!fields.find(f => f.id === contextField.id)) {
                fields.push(contextField);
            }
        });
        
        return fields;
    };

    // Map EMR field types to report field types
    const mapEMRFieldType = (emrType) => {
        const typeMap = {
            'text': 'text',
            'textarea': 'text',
            'number': 'number',
            'date': 'date',
            'datetime': 'datetime',
            'time': 'time',
            'select': 'select',
            'dropdown': 'select',
            'radio': 'select',
            'checkbox': 'boolean',
            'checkboxes': 'multiselect',
            'file': 'file',
            'email': 'email',
            'phone': 'phone',
            'url': 'url'
        };
        
        return typeMap[emrType] || 'text';
    };

    // Handle department selection
    const handleDepartmentChange = async (departmentId) => {
        setSelectedDepartment(departmentId);
        setSelectedCategory('');
        setSelectedForm(null);
        
        if (departmentId) {
            try {
                // Load categories for selected department
                const categoriesResult = await reportingService.getEMRCategories(departmentId);
                if (categoriesResult.success) {
                    setEmrCategories(categoriesResult.data);
                }
                
                // Load forms for selected department
                const formsResult = await reportingService.getEMRForms({ 
                    department_id: departmentId, 
                    active_only: true 
                });
                if (formsResult.success) {
                    setEmrForms(formsResult.data);
                }
            } catch (error) {
                toast.error('Failed to load department data');
            }
        }
    };

    // Handle category selection
    const handleCategoryChange = async (categoryId) => {
        setSelectedCategory(categoryId);
        setSelectedForm(null);
        
        if (categoryId) {
            try {
                // Load forms for selected category
                const formsResult = await reportingService.getEMRForms({ 
                    department_id: selectedDepartment,
                    category_id: categoryId,
                    active_only: true 
                });
                if (formsResult.success) {
                    setEmrForms(formsResult.data);
                }
            } catch (error) {
                toast.error('Failed to load category forms');
            }
        }
    };
    
    // Extract fields from template configuration
    const extractFieldsFromTemplate = (template) => {
        const fields = [];
        
        // Check for available_filters which often contain field definitions
        if (template.available_filters && Array.isArray(template.available_filters)) {
            template.available_filters.forEach(filterField => {
                fields.push({
                    id: filterField,
                    label: filterField.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                    type: 'text'
                });
            });
        }
        
        // Check for query_template or output_template for additional fields
        if (template.query_template || template.output_template) {
            // Add common healthcare fields based on template category
            const categoryFields = getCategoryFields(template.category);
            categoryFields.forEach(field => {
                if (!fields.find(f => f.id === field.id)) {
                    fields.push(field);
                }
            });
        }
        
        // If no specific fields found, return default fields for the category
        if (fields.length === 0) {
            return getCategoryFields(template.category || 'patient');
        }
        
        return fields;
    };

    // Get default fields based on template category
    const getCategoryFields = (category) => {
        const fieldMap = {
            'patient': [
                { id: 'patient_name', label: 'Patient Name', type: 'text' },
                { id: 'patient_id', label: 'Patient ID', type: 'text' },
                { id: 'date_of_birth', label: 'Date of Birth', type: 'date' },
                { id: 'admission_date', label: 'Admission Date', type: 'date' },
                { id: 'diagnosis', label: 'Diagnosis', type: 'text' },
                { id: 'doctor', label: 'Doctor', type: 'text' }
            ],
            'facility': [
                { id: 'facility_name', label: 'Facility Name', type: 'text' },
                { id: 'department', label: 'Department', type: 'text' },
                { id: 'staff_count', label: 'Staff Count', type: 'number' },
                { id: 'capacity', label: 'Capacity', type: 'number' }
            ],
            'medpass': [
                { id: 'medication_name', label: 'Medication', type: 'text' },
                { id: 'dosage', label: 'Dosage', type: 'text' },
                { id: 'patient_name', label: 'Patient Name', type: 'text' },
                { id: 'administration_time', label: 'Administration Time', type: 'datetime' }
            ],
            'hemodialysis': [
                { id: 'patient_name', label: 'Patient Name', type: 'text' },
                { id: 'session_date', label: 'Session Date', type: 'date' },
                { id: 'duration', label: 'Duration (hours)', type: 'number' },
                { id: 'fluid_removed', label: 'Fluid Removed (ml)', type: 'number' }
            ]
        };
        
        return fieldMap[category] || fieldMap['patient'];
    };

    // Handle template selection
    const handleTemplateSelect = (template) => {
        setSelectedTemplate(template);
        setTemplateSelected(true);
        
        if (template) {
            // Extract fields from template
            const templateFields = extractFieldsFromTemplate(template);
            
            setReportConfig(prev => ({
                ...prev,
                templateId: template.id,
                name: template.name ? `${template.name} - Report` : prev.name,
                type: template.type || template.category || prev.type,
                fields: templateFields, // Auto-fill fields from template
                description: template.description || prev.description
            }));
            // Auto-advance to next step after template selection
            setActiveStep(3);
            toast.success(`Template "${template.name}" selected with ${templateFields.length} fields`);
        } else {
            // "Start from scratch" option
            setReportConfig(prev => ({
                ...prev,
                templateId: null,
                name: prev.name,
                type: 'standard',
                fields: [],
                description: prev.description
            }));
            // Auto-advance to next step
            setActiveStep(3);
            toast.success('Starting with blank template');
        }
    };
    
    // Create new filter
    const createFilter = () => {
        if (!newFilter.field || !newFilter.value) {
            toast.error('Please select a field and enter a value');
            return;
        }

        const filter = {
            ...newFilter,
            id: Date.now(),
            name: `${newFilter.field} ${filterOperators.find(op => op.value === newFilter.operator)?.label} ${newFilter.value}`,
            description: `Filter by ${newFilter.field.replace(/_/g, ' ')} ${filterOperators.find(op => op.value === newFilter.operator)?.label.toLowerCase()} "${newFilter.value}"`
        };

        setReportConfig(prev => ({
            ...prev,
            filters: [...prev.filters, filter]
        }));

        // Reset filter form
        setNewFilter({
            field: '',
            operator: 'eq',
            value: '',
            data_type: 'string'
        });

        toast.success('Filter added successfully');
    };
    
    // Remove filter from report
    const removeFilter = (filterId) => {
        setReportConfig(prev => ({
            ...prev,
            filters: prev.filters.filter(f => f.id !== filterId)
        }));
        toast.success('Filter removed');
    };

    // Get available fields for filtering (combine template fields and default fields)
    const getAvailableFilterFields = () => {
        const allFields = [...availableFields];
        
        // Add fields from selected template if any
        if (selectedTemplate && selectedTemplate.available_filters) {
            selectedTemplate.available_filters.forEach(filterField => {
                if (!allFields.find(f => f.id === filterField)) {
                    allFields.push({
                        id: filterField,
                        label: filterField.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                        type: 'text'
                    });
                }
            });
        }

        // Add fields from EMR form if selected
        if (formFields && formFields.length > 0) {
            formFields.forEach(formField => {
                if (!allFields.find(f => f.id === formField.id)) {
                    allFields.push(formField);
                }
            });
        }

        return allFields;
    };

    // Get all available fields for selection (combines static fields with EMR form fields)
    const getAllAvailableFields = () => {
        const allFields = [...availableFields];
        
        // Add EMR form fields if available
        if (formFields && formFields.length > 0) {
            formFields.forEach(formField => {
                if (!allFields.find(f => f.id === formField.id)) {
                    allFields.push(formField);
                }
            });
        }

        // Add template fields if available
        if (selectedTemplate && selectedTemplate.available_filters) {
            selectedTemplate.available_filters.forEach(filterField => {
                if (!allFields.find(f => f.id === filterField)) {
                    allFields.push({
                        id: filterField,
                        label: filterField.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                        type: 'text'
                    });
                }
            });
        }

        return allFields;
    };
    
    // Add field to report
    const addField = (field) => {
        if (!reportConfig.fields.find(f => f.id === field.id)) {
            setReportConfig(prev => ({
                ...prev,
                fields: [...prev.fields, field]
            }));
        }
    };
    
    // Remove field from report
    const removeField = (fieldId) => {
        setReportConfig(prev => ({
            ...prev,
            fields: prev.fields.filter(f => f.id !== fieldId)
        }));
    };
    
    // Preview report
    const handlePreview = async () => {
        try {
            setShowPreview(true);
            // Generate preview using actual backend service
            const response = await reportingService.previewTemplate(reportConfig.id, {
                name: reportConfig.name,
                type: reportConfig.type,
                fields: reportConfig.fields,
                filters: reportConfig.filters
            });
            
            if (response.success) {
                setPreviewData(response.data);
            } else {
                // Fallback to basic structure if preview fails
                setPreviewData({
                    columns: reportConfig.fields,
                    rows: [],
                    message: 'Preview data not available'
                });
            }
        } catch (error) {
            toast.error('Failed to generate preview');
            setPreviewData({
                columns: reportConfig.fields,
                rows: [],
                message: 'Preview not available'
            });
        }
    };
    
    // Save report configuration
    const handleSaveReport = async () => {
        try {
            if (!reportConfig.name.trim()) {
                toast.error('Please enter a report name');
                return;
            }
            
            const response = await dispatch(createReport(reportConfig));
            if (response.meta.requestStatus === 'fulfilled') {
                toast.success('Report saved successfully');
                // Reset form or redirect
            }
        } catch (error) {
            toast.error('Failed to save report');
        }
    };
    
    // Generate and download report
    const handleGenerateReport = async () => {
        try {
            // Validate all required fields
            if (!validateStep(1) || !validateStep(3)) {
                toast.error('Please complete all required fields before generating the report');
                return;
            }
            
            // Prepare report generation request according to backend schema
            const reportRequest = {
                template_id: reportConfig.templateId || 'custom_report',
                filters: {
                    conditions: reportConfig.filters.map(filter => ({
                        field: filter.field || filter.name,
                        operator: filter.operator || 'eq',
                        value: filter.value,
                        data_type: filter.data_type || 'string'
                    })),
                    logic_operator: 'and',
                    date_range: {
                        start_date: reportConfig.dateRange.start,
                        end_date: reportConfig.dateRange.end || new Date().toISOString().split('T')[0]
                    }
                },
                export_format: 'json',
                parameters: {
                    name: reportConfig.name,
                    description: reportConfig.description,
                    type: reportConfig.type,
                    fields: reportConfig.fields,
                    format: reportConfig.format
                }
            };
            
            // Generate the report using the reporting service
            const response = await dispatch(generateReportFromTemplate(reportRequest));
            if (response.meta.requestStatus === 'fulfilled') {
                toast.success('Report generation started successfully');
                // Reset form or redirect to reports list
                // setReportConfig({...initialState}); // Optionally reset
            } else {
                toast.error('Failed to generate report: ' + (response.error?.message || 'Unknown error'));
            }
        } catch (error) {
            toast.error('Failed to generate report');
        }
    };
    
    // Step validation logic
    const validateStep = (stepNumber) => {
        switch (stepNumber) {
            case 1:
                if (!reportConfig.name.trim()) {
                    toast.error('Please enter a report name');
                    return false;
                }
                if (!reportConfig.dateRange.start) {
                    toast.error('Please select a start date');
                    return false;
                }
                return true;
            case 2:
                // Template step is optional, but we need to complete it to proceed
                return true;
            case 3:
                if (reportConfig.fields.length === 0) {
                    toast.error('Please select at least one data field');
                    return false;
                }
                return true;
            case 4:
                // Filters are optional
                return true;
            case 5:
                // Preview step - validate all previous steps
                return validateStep(1) && validateStep(3);
            default:
                return true;
        }
    };

    // Navigate to specific step with validation
    const navigateToStep = (stepNumber) => {
        // If going backwards, allow without validation
        if (stepNumber < activeStep) {
            setActiveStep(stepNumber);
            return;
        }
        
        // If going forwards, validate current step
        if (validateStep(activeStep)) {
            setActiveStep(stepNumber);
        }
    };

    // Go to next step with validation
    const goToNextStep = () => {
        if (activeStep < 5 && validateStep(activeStep)) {
            setActiveStep(activeStep + 1);
        }
    };

    // Go to previous step
    const goToPreviousStep = () => {
        if (activeStep > 1) {
            setActiveStep(activeStep - 1);
        }
    };

    // Check if step is completed
    const isStepCompleted = (stepNumber) => {
        switch (stepNumber) {
            case 1:
                return reportConfig.name.trim() && reportConfig.dateRange.start;
            case 2:
                return templateSelected; // Template selection step completed
            case 3:
                return reportConfig.fields.length > 0;
            case 4:
                return true; // Filters are optional
            case 5:
                return isStepCompleted(1) && isStepCompleted(3);
            default:
                return false;
        }
    };

    const steps = [
        { id: 1, title: 'Basic Info', description: 'Report name and type' },
        { id: 2, title: 'Template', description: 'Choose a template' },
        { id: 3, title: 'Fields', description: 'Select data fields' },
        { id: 4, title: 'Filters', description: 'Add filters' },
        { id: 5, title: 'Preview', description: 'Review and generate' }
    ];
    
    return (
        <ErrorBoundary fallbackMessage="Unable to load the report builder. Please refresh the page or contact support.">
            <ServiceStatus 
                fallbackComponent={<ReportingFallback />}
                showStatus={true}
                autoRetry={false}
            >
                <div className="report-builder">
            <Container fluid className="p-4">
                {/* Header */}
                <Row className="mb-4">
                    <Col>
                        <div className="d-flex justify-content-between align-items-center">
                            <div>
                                <h2 className="text-primary fw-bold mb-1">Report Builder</h2>
                                <p className="text-muted mb-0">Create custom reports with interactive tools</p>
                            </div>
                            <div className="d-flex gap-2">
                                <Button 
                                    variant="outline-primary" 
                                    size="sm"
                                    onClick={handlePreview}
                                    disabled={!reportConfig.name || reportConfig.fields.length === 0}
                                >
                                    <BsEye className="me-1" />
                                    Preview
                                </Button>
                                <Button 
                                    variant="success" 
                                    size="sm"
                                    onClick={handleSaveReport}
                                    disabled={loading || !reportConfig.name}
                                >
                                    {loading ? <Spinner animation="border" size="sm" className="me-1" /> : <BsSave className="me-1" />}
                                    Save Report
                                </Button>
                                <Button 
                                    variant="primary" 
                                    size="sm"
                                    onClick={handleGenerateReport}
                                    disabled={loading || !reportConfig.name}
                                >
                                    <BsDownload className="me-1" />
                                    Generate
                                </Button>
                            </div>
                        </div>
                    </Col>
                </Row>
                
                {/* Progress Steps */}
                <Row className="mb-4">
                    <Col>
                        <Card className="border-0 shadow-sm">
                            <Card.Body className="py-3">
                                <div className="d-flex justify-content-between align-items-center">
                                    {steps.map((step, index) => (
                                        <div key={step.id} className="d-flex align-items-center">
                                            <div 
                                                className={`step-circle ${
                                                    activeStep === step.id ? 'active' : 
                                                    isStepCompleted(step.id) ? 'completed' : 
                                                    activeStep > step.id ? 'visited' : ''
                                                }`}
                                                onClick={() => navigateToStep(step.id)}
                                                style={{ cursor: 'pointer' }}
                                            >
                                                {isStepCompleted(step.id) && activeStep > step.id ? (
                                                    <BsCheck />
                                                ) : (
                                                    step.id
                                                )}
                                            </div>
                                            <div className="ms-2">
                                                <small className="fw-semibold d-block">{step.title}</small>
                                                <small className="text-muted">{step.description}</small>
                                            </div>
                                            {index < steps.length - 1 && (
                                                <div className={`step-line ${activeStep > step.id ? 'active' : ''}`} />
                                            )}
                                        </div>
                                    ))}
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
                
                {/* Main Content */}
                <Row>
                    <Col lg={8}>
                        <Card className="border-0 shadow-sm h-100">
                            <Card.Body>
                                {/* Step 1: Basic Info */}
                                {activeStep === 1 && (
                                    <div className="step-content">
                                        <h5 className="mb-4">Basic Information</h5>
                                        <Form>
                                            <Row>
                                                <Col md={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>Report Name *</Form.Label>
                                                        <Form.Control
                                                            type="text"
                                                            placeholder="Enter report name"
                                                            value={reportConfig.name}
                                                            onChange={(e) => updateReportConfig('name', e.target.value)}
                                                            className={!reportConfig.name.trim() && activeStep > 1 ? 'is-invalid' : ''}
                                                        />
                                                        {!reportConfig.name.trim() && activeStep > 1 && (
                                                            <div className="invalid-feedback">Report name is required</div>
                                                        )}
                                                    </Form.Group>
                                                </Col>
                                                <Col md={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>Report Type</Form.Label>
                                                        <Form.Select
                                                            value={reportConfig.type}
                                                            onChange={(e) => updateReportConfig('type', e.target.value)}
                                                        >
                                                            <option value="standard">Standard Report</option>
                                                            <option value="summary">Summary Report</option>
                                                            <option value="detailed">Detailed Report</option>
                                                            <option value="analytics">Analytics Report</option>
                                                        </Form.Select>
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                            <Form.Group className="mb-3">
                                                <Form.Label>Description</Form.Label>
                                                <Form.Control
                                                    as="textarea"
                                                    rows={3}
                                                    placeholder="Enter report description"
                                                    value={reportConfig.description}
                                                    onChange={(e) => updateReportConfig('description', e.target.value)}
                                                />
                                            </Form.Group>
                                            <Row>
                                                <Col md={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>Start Date *</Form.Label>
                                                        <Form.Control
                                                            type="date"
                                                            value={reportConfig.dateRange.start}
                                                            onChange={(e) => updateReportConfig('dateRange', {
                                                                ...reportConfig.dateRange,
                                                                start: e.target.value
                                                            })}
                                                            className={!reportConfig.dateRange.start && activeStep > 1 ? 'is-invalid' : ''}
                                                        />
                                                        {!reportConfig.dateRange.start && activeStep > 1 && (
                                                            <div className="invalid-feedback">Start date is required</div>
                                                        )}
                                                    </Form.Group>
                                                </Col>
                                                <Col md={6}>
                                                    <Form.Group className="mb-3">
                                                        <Form.Label>End Date</Form.Label>
                                                        <Form.Control
                                                            type="date"
                                                            value={reportConfig.dateRange.end}
                                                            onChange={(e) => updateReportConfig('dateRange', {
                                                                ...reportConfig.dateRange,
                                                                end: e.target.value
                                                            })}
                                                        />
                                                    </Form.Group>
                                                </Col>
                                            </Row>
                                        </Form>
                                    </div>
                                )}
                                
                                {/* Step 2: Template Selection */}
                                {activeStep === 2 && (
                                    <div className="step-content">
                                        <h5 className="mb-4">Choose Template Source</h5>
                                        
                                        {/* EMR Sync Status */}
                                        {emrSyncStatus && (
                                            <Alert 
                                                variant={emrSyncStatus.status === 'connected' ? 'success' : 'warning'} 
                                                className="mb-4"
                                            >
                                                <div className="d-flex align-items-center">
                                                    <BsCheck className="me-2" />
                                                    <div>
                                                        <strong>EMR Integration Status:</strong> {emrSyncStatus.status}
                                                        {emrSyncStatus.statistics && (
                                                            <div className="small mt-1">
                                                                {emrSyncStatus.statistics.total_active_forms} active forms available
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            </Alert>
                                        )}

                                        {/* Template Source Selection */}
                                        <Tabs 
                                            activeKey={templateSource} 
                                            onSelect={(k) => setTemplateSource(k)}
                                            className="mb-4"
                                        >
                                            <Tab eventKey="static" title="Pre-built Templates">
                                                <Row className="mt-3">
                                                    <Col md={6}>
                                                        <Card 
                                                            className={`template-option ${!selectedTemplate && templateSource === 'static' ? 'selected border-primary' : 'border-light'}`}
                                                            onClick={() => handleTemplateSelect(null)}
                                                            style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                                                        >
                                                            <Card.Body className="text-center py-4">
                                                                <BsFileText className={`display-4 mb-3 ${!selectedTemplate && templateSource === 'static' ? 'text-primary' : 'text-muted'}`} />
                                                                <h6 className={!selectedTemplate && templateSource === 'static' ? 'text-primary' : ''}>Start from Scratch</h6>
                                                                <p className="text-muted small mb-0">Build a custom report from the ground up</p>
                                                                {!selectedTemplate && templateSource === 'static' && (
                                                                    <Badge bg="primary" className="mt-2">Selected</Badge>
                                                                )}
                                                            </Card.Body>
                                                        </Card>
                                                    </Col>
                                                    {loading ? (
                                                        <Col md={6}>
                                                            <div className="text-center py-4">
                                                                <Spinner animation="border" className="text-primary" />
                                                                <p className="mt-2 text-muted">Loading templates...</p>
                                                            </div>
                                                        </Col>
                                                    ) : templates && templates.length > 0 ? (
                                                        templates.map(template => (
                                                            <Col md={6} key={template.id} className="mb-3">
                                                                <Card 
                                                                    className={`template-option ${selectedTemplate?.id === template.id ? 'selected border-primary' : 'border-light'}`}
                                                                    onClick={() => handleTemplateSelect(template)}
                                                                    style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                                                                >
                                                                    <Card.Body>
                                                                        <div className="d-flex align-items-start">
                                                                            <BsBarChart className={`mt-1 me-3 ${selectedTemplate?.id === template.id ? 'text-primary' : 'text-muted'}`} />
                                                                            <div className="flex-grow-1">
                                                                                <h6 className={`mb-1 ${selectedTemplate?.id === template.id ? 'text-primary' : ''}`}>
                                                                                    {template.name}
                                                                                </h6>
                                                                                <p className="text-muted small mb-2">
                                                                                    {typeof template.description === 'string' 
                                                                                        ? template.description 
                                                                                        : 'No description available'
                                                                                    }
                                                                                </p>
                                                                                <div className="d-flex justify-content-between align-items-center">
                                                                                    <Badge bg="secondary" className="small">
                                                                                        {template.type || template.category || 'Standard'}
                                                                                    </Badge>
                                                                                    {selectedTemplate?.id === template.id && (
                                                                                        <Badge bg="primary">Selected</Badge>
                                                                                    )}
                                                                                </div>
                                                                            </div>
                                                                        </div>
                                                                    </Card.Body>
                                                                </Card>
                                                            </Col>
                                                        ))
                                                    ) : (
                                                        <Col md={6}>
                                                            <div className="text-center py-4">
                                                                <BsBarChart className="display-4 text-muted mb-3" />
                                                                <p className="text-muted">No templates available</p>
                                                                <small className="text-muted">You can still create a custom report from scratch</small>
                                                            </div>
                                                        </Col>
                                                    )}
                                                </Row>
                                            </Tab>
                                            
                                            <Tab 
                                                eventKey="emr" 
                                                title={
                                                    <span>
                                                        EMR Forms
                                                        {emrSyncStatus?.status !== 'connected' && (
                                                            <Badge bg="warning" className="ms-1">Disconnected</Badge>
                                                        )}
                                                    </span>
                                                }
                                                disabled={emrSyncStatus?.status !== 'connected'}
                                            >
                                                <div className="mt-3">
                                                    {emrLoading && (
                                                        <div className="text-center py-4">
                                                            <Spinner animation="border" className="text-primary" />
                                                            <p className="mt-2 text-muted">Loading EMR forms...</p>
                                                        </div>
                                                    )}
                                                    
                                                    {!emrLoading && emrSyncStatus?.status === 'connected' && (
                                                        <>
                                                            {/* Department and Category Filters */}
                                                            <Row className="mb-4">
                                                                <Col md={6}>
                                                                    <Form.Group>
                                                                        <Form.Label>Department</Form.Label>
                                                                        <Form.Select 
                                                                            value={selectedDepartment}
                                                                            onChange={(e) => handleDepartmentChange(e.target.value)}
                                                                        >
                                                                            <option value="">All Departments</option>
                                                                            {emrDepartments.map(dept => (
                                                                                <option key={dept.id || dept.department_id} value={dept.id || dept.department_id}>
                                                                                    {dept.name || dept.department_name}
                                                                                </option>
                                                                            ))}
                                                                        </Form.Select>
                                                                    </Form.Group>
                                                                </Col>
                                                                <Col md={6}>
                                                                    <Form.Group>
                                                                        <Form.Label>Category</Form.Label>
                                                                        <Form.Select 
                                                                            value={selectedCategory}
                                                                            onChange={(e) => handleCategoryChange(e.target.value)}
                                                                        >
                                                                            <option value="">All Categories</option>
                                                                            {emrCategories.map(cat => (
                                                                                <option key={cat.id || cat.category_id} value={cat.id || cat.category_id}>
                                                                                    {cat.name || cat.category_name}
                                                                                </option>
                                                                            ))}
                                                                        </Form.Select>
                                                                    </Form.Group>
                                                                </Col>
                                                            </Row>

                                                            {/* EMR Forms List */}
                                                            <Row>
                                                                {emrForms && emrForms.length > 0 ? (
                                                                    emrForms.map(form => (
                                                                        <Col md={6} key={form.id || form.form_id} className="mb-3">
                                                                            <Card 
                                                                                className={`template-option ${selectedForm?.id === form.id || selectedForm?.form_id === form.form_id ? 'selected border-primary' : 'border-light'}`}
                                                                                onClick={() => handleEMRFormSelect(form)}
                                                                                style={{ cursor: 'pointer', transition: 'all 0.2s' }}
                                                                            >
                                                                                <Card.Body>
                                                                                    <div className="d-flex align-items-start">
                                                                                        <BsFileText className={`mt-1 me-3 ${selectedForm?.id === form.id || selectedForm?.form_id === form.form_id ? 'text-primary' : 'text-muted'}`} />
                                                                                        <div className="flex-grow-1">
                                                                                            <h6 className={`mb-1 ${selectedForm?.id === form.id || selectedForm?.form_id === form.form_id ? 'text-primary' : ''}`}>
                                                                                                {form.name || form.form_name}
                                                                                            </h6>
                                                                                            <p className="text-muted small mb-2">
                                                                                                {form.description || 'Clinical form from EMR system'}
                                                                                            </p>
                                                                                            <div className="d-flex justify-content-between align-items-center">
                                                                                                <div>
                                                                                                    <Badge bg="info" className="small me-1">
                                                                                                        {form.category_name || form.category_id || 'Clinical'}
                                                                                                    </Badge>
                                                                                                    {form.department_name && (
                                                                                                        <Badge bg="secondary" className="small">
                                                                                                            {form.department_name}
                                                                                                        </Badge>
                                                                                                    )}
                                                                                                </div>
                                                                                                {(selectedForm?.id === form.id || selectedForm?.form_id === form.form_id) && (
                                                                                                    <Badge bg="primary">Selected</Badge>
                                                                                                )}
                                                                                            </div>
                                                                                            {form.field_count && (
                                                                                                <small className="text-muted d-block mt-1">
                                                                                                    {form.field_count} fields available
                                                                                                </small>
                                                                                            )}
                                                                                        </div>
                                                                                    </div>
                                                                                </Card.Body>
                                                                            </Card>
                                                                        </Col>
                                                                    ))
                                                                ) : (
                                                                    <Col md={12}>
                                                                        <div className="text-center py-4">
                                                                            <BsSearch className="display-4 text-muted mb-3" />
                                                                            <p className="text-muted">No forms found</p>
                                                                            <small className="text-muted">
                                                                                Try adjusting your department or category filters
                                                                            </small>
                                                                        </div>
                                                                    </Col>
                                                                )}
                                                            </Row>
                                                        </>
                                                    )}
                                                </div>
                                            </Tab>
                                        </Tabs>
                                    </div>
                                )}
                                
                                {/* Step 3: Field Selection */}
                                {activeStep === 3 && (
                                    <div className="step-content">
                                        <h5 className="mb-4">Select Data Fields</h5>
                                        <Row>
                                            <Col md={6}>
                                                <h6 className="mb-3">
                                                    Available Fields 
                                                    {templateSource === 'emr' && selectedForm && (
                                                        <Badge bg="info" className="ms-2">
                                                            From {selectedForm.name || selectedForm.form_name}
                                                        </Badge>
                                                    )}
                                                </h6>
                                                <div className="available-fields" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                                                    {getAllAvailableFields().map(field => (
                                                        <div 
                                                            key={field.id}
                                                            className="field-item p-2 mb-2 border rounded"
                                                            onClick={() => addField(field)}
                                                            style={{ cursor: 'pointer' }}
                                                        >
                                                            <div className="d-flex justify-content-between align-items-center">
                                                                <div className="flex-grow-1">
                                                                    <span>{field.label}</span>
                                                                    {field.category && (
                                                                        <Badge 
                                                                            bg={field.category === 'context' ? 'secondary' : 'info'} 
                                                                            className="ms-2 small"
                                                                        >
                                                                            {field.category}
                                                                        </Badge>
                                                                    )}
                                                                </div>
                                                                <BsPlus className="text-primary" />
                                                            </div>
                                                            <small className="text-muted">
                                                                {field.type}
                                                                {field.required && <span className="text-danger"> *</span>}
                                                            </small>
                                                        </div>
                                                    ))}
                                                </div>
                                            </Col>
                                            <Col md={6}>
                                                <h6 className="mb-3">
                                                    Selected Fields ({reportConfig.fields.length})
                                                    {reportConfig.fields.length === 0 && activeStep > 3 && (
                                                        <Badge bg="danger" className="ms-2">Required</Badge>
                                                    )}
                                                </h6>
                                                <div className={`selected-fields ${reportConfig.fields.length === 0 && activeStep > 3 ? 'border border-danger rounded p-2' : ''}`}>
                                                    {reportConfig.fields.length === 0 ? (
                                                        <div className="text-center py-4">
                                                            <BsTable className={`display-4 mb-3 ${activeStep > 3 ? 'text-danger' : 'text-muted'}`} />
                                                            <p className={activeStep > 3 ? 'text-danger' : 'text-muted'}>
                                                                {activeStep > 3 ? 'Please select at least one field' : 'No fields selected'}
                                                            </p>
                                                        </div>
                                                    ) : (
                                                        reportConfig.fields.map(field => (
                                                            <div 
                                                                key={field.id}
                                                                className="field-item p-2 mb-2 border rounded bg-light"
                                                            >
                                                                <div className="d-flex justify-content-between align-items-center">
                                                                    <span>{field.label}</span>
                                                                    <Button
                                                                        variant="link"
                                                                        size="sm"
                                                                        className="text-danger p-0"
                                                                        onClick={() => removeField(field.id)}
                                                                    >
                                                                        <BsXCircle />
                                                                    </Button>
                                                                </div>
                                                                <small className="text-muted">{field.type}</small>
                                                            </div>
                                                        ))
                                                    )}
                                                </div>
                                            </Col>
                                        </Row>
                                    </div>
                                )}
                                
                                {/* Step 4: Filters */}
                                {activeStep === 4 && (
                                    <div className="step-content">
                                        <h5 className="mb-4">Add Filters</h5>
                                        <Row>
                                            <Col md={6}>
                                                <h6 className="mb-3">Create New Filter</h6>
                                                <Card className="border-light">
                                                    <Card.Body>
                                                        <Form>
                                                            <Form.Group className="mb-3">
                                                                <Form.Label>Field</Form.Label>
                                                                <Form.Select
                                                                    value={newFilter.field}
                                                                    onChange={(e) => setNewFilter(prev => ({
                                                                        ...prev,
                                                                        field: e.target.value,
                                                                        data_type: getAvailableFilterFields().find(f => f.id === e.target.value)?.type || 'string'
                                                                    }))}
                                                                >
                                                                    <option value="">Select a field</option>
                                                                    {getAvailableFilterFields().map(field => (
                                                                        <option key={field.id} value={field.id}>
                                                                            {field.label}
                                                                        </option>
                                                                    ))}
                                                                </Form.Select>
                                                            </Form.Group>
                                                            
                                                            <Form.Group className="mb-3">
                                                                <Form.Label>Operator</Form.Label>
                                                                <Form.Select
                                                                    value={newFilter.operator}
                                                                    onChange={(e) => setNewFilter(prev => ({
                                                                        ...prev,
                                                                        operator: e.target.value
                                                                    }))}
                                                                >
                                                                    {filterOperators.map(op => (
                                                                        <option key={op.value} value={op.value}>
                                                                            {op.label}
                                                                        </option>
                                                                    ))}
                                                                </Form.Select>
                                                            </Form.Group>
                                                            
                                                            <Form.Group className="mb-3">
                                                                <Form.Label>Value</Form.Label>
                                                                <Form.Control
                                                                    type={newFilter.data_type === 'date' ? 'date' : 
                                                                          newFilter.data_type === 'number' ? 'number' : 'text'}
                                                                    placeholder="Enter filter value"
                                                                    value={newFilter.value}
                                                                    onChange={(e) => setNewFilter(prev => ({
                                                                        ...prev,
                                                                        value: e.target.value
                                                                    }))}
                                                                />
                                                            </Form.Group>
                                                            
                                                            <Button
                                                                variant="primary"
                                                                onClick={createFilter}
                                                                disabled={!newFilter.field || !newFilter.value}
                                                                className="w-100"
                                                            >
                                                                <BsPlus className="me-1" />
                                                                Add Filter
                                                            </Button>
                                                        </Form>
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                            
                                            <Col md={6}>
                                                <h6 className="mb-3">Applied Filters ({reportConfig.filters.length})</h6>
                                                {reportConfig.filters.length === 0 ? (
                                                    <div className="text-center py-4">
                                                        <BsFilter className="display-4 text-muted mb-3" />
                                                        <p className="text-muted">No filters applied</p>
                                                        <small className="text-muted">Filters are optional and help narrow down your report data</small>
                                                    </div>
                                                ) : (
                                                    <div className="applied-filters">
                                                        {reportConfig.filters.map(filter => (
                                                            <div 
                                                                key={filter.id}
                                                                className="filter-item p-3 mb-2 border rounded bg-light"
                                                            >
                                                                <div className="d-flex justify-content-between align-items-start">
                                                                    <div className="flex-grow-1">
                                                                        <h6 className="mb-1">{filter.name}</h6>
                                                                        <small className="text-muted">{filter.description}</small>
                                                                        <div className="mt-2">
                                                                            <Badge bg="secondary" className="me-1">{filter.field}</Badge>
                                                                            <Badge bg="primary" className="me-1">{filter.operator}</Badge>
                                                                            <Badge bg="success">{filter.value}</Badge>
                                                                        </div>
                                                                    </div>
                                                                    <Button
                                                                        variant="link"
                                                                        size="sm"
                                                                        className="text-danger p-0"
                                                                        onClick={() => removeFilter(filter.id)}
                                                                        title="Remove filter"
                                                                    >
                                                                        <BsXCircle />
                                                                    </Button>
                                                                </div>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </Col>
                                        </Row>
                                    </div>
                                )}
                                
                                {/* Step 5: Preview & Generate */}
                                {activeStep === 5 && (
                                    <div className="step-content">
                                        <h5 className="mb-4">Review & Generate Report</h5>
                                        <Row>
                                            <Col md={6}>
                                                <Card className="mb-3">
                                                    <Card.Header>
                                                        <h6 className="mb-0">Report Summary</h6>
                                                    </Card.Header>
                                                    <Card.Body>
                                                        <div className="summary-item mb-2">
                                                            <strong>Name:</strong> {reportConfig.name || 'Untitled Report'}
                                                        </div>
                                                        <div className="summary-item mb-2">
                                                            <strong>Type:</strong> {reportConfig.type}
                                                        </div>
                                                        <div className="summary-item mb-2">
                                                            <strong>Fields:</strong> {reportConfig.fields.length} selected
                                                        </div>
                                                        <div className="summary-item mb-2">
                                                            <strong>Filters:</strong> {reportConfig.filters.length} applied
                                                            {reportConfig.filters.length > 0 && (
                                                                <div className="mt-1">
                                                                    {reportConfig.filters.map((filter, index) => (
                                                                        <Badge key={filter.id} bg="secondary" className="me-1 mb-1">
                                                                            {filter.field} {filter.operator} {filter.value}
                                                                        </Badge>
                                                                    ))}
                                                                </div>
                                                            )}
                                                        </div>
                                                        {reportConfig.dateRange.start && (
                                                            <div className="summary-item mb-2">
                                                                <strong>Date Range:</strong> {reportConfig.dateRange.start} to {reportConfig.dateRange.end || 'Now'}
                                                            </div>
                                                        )}
                                                    </Card.Body>
                                                </Card>
                                            </Col>
                                            <Col md={6}>
                                                <div className="d-flex flex-column gap-2">
                                                    <Button 
                                                        variant="outline-primary"
                                                        onClick={handlePreview}
                                                        disabled={!reportConfig.name || reportConfig.fields.length === 0}
                                                    >
                                                        <BsEye className="me-2" />
                                                        Preview Report
                                                    </Button>
                                                    <Button 
                                                        variant="success"
                                                        onClick={handleSaveReport}
                                                        disabled={loading || !reportConfig.name}
                                                    >
                                                        <BsSave className="me-2" />
                                                        Save Configuration
                                                    </Button>
                                                    <Button 
                                                        variant="primary"
                                                        onClick={handleGenerateReport}
                                                        disabled={loading || !reportConfig.name}
                                                    >
                                                        <BsDownload className="me-2" />
                                                        Generate Report
                                                    </Button>
                                                </div>
                                            </Col>
                                        </Row>
                                    </div>
                                )}
                                
                                {/* Navigation Buttons */}
                                <div className="d-flex justify-content-between mt-4 pt-3 border-top">
                                    <Button 
                                        variant="outline-secondary"
                                        onClick={goToPreviousStep}
                                        disabled={activeStep === 1}
                                    >
                                        Previous
                                    </Button>
                                    <Button 
                                        variant="primary"
                                        onClick={goToNextStep}
                                        disabled={activeStep === 5}
                                    >
                                        Next
                                    </Button>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                    
                    {/* Sidebar */}
                    <Col lg={4}>
                        <Card className="border-0 shadow-sm sticky-top">
                            <Card.Header>
                                <h6 className="mb-0">Quick Actions</h6>
                            </Card.Header>
                            <Card.Body>
                                <div className="d-grid gap-2">
                                    <Button 
                                        variant="outline-primary" 
                                        size="sm"
                                        onClick={() => setActiveStep(2)}
                                        disabled={!validateStep(1)}
                                    >
                                        <BsFileText className="me-2" />
                                        Load Template
                                    </Button>
                                    <Button 
                                        variant="outline-success" 
                                        size="sm"
                                        onClick={handleSaveReport}
                                        disabled={loading || !reportConfig.name}
                                    >
                                        <BsSave className="me-2" />
                                        Save Configuration
                                    </Button>
                                    <Button 
                                        variant="outline-info" 
                                        size="sm"
                                        onClick={handlePreview}
                                        disabled={!reportConfig.name || reportConfig.fields.length === 0}
                                    >
                                        <BsEye className="me-2" />
                                        Preview Report
                                    </Button>
                                </div>
                                
                                {reportConfig.name && (
                                    <Alert variant="info" className="mt-3 small">
                                        <strong>Tip:</strong> Your report configuration is automatically saved as you build it.
                                    </Alert>
                                )}
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>
            </Container>
            
            {/* Preview Modal */}
            <Modal show={showPreview} onHide={() => setShowPreview(false)} size="xl" centered>
                <Modal.Header closeButton>
                    <Modal.Title>Report Preview</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {previewData ? (
                        <div className="table-responsive">
                            <table className="table table-striped">
                                <thead>
                                    <tr>
                                        {previewData.columns.map(col => (
                                            <th key={col.id}>{col.label}</th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {previewData.rows.map((row, index) => (
                                        <tr key={index}>
                                            {previewData.columns.map(col => (
                                                <td key={col.id}>{row[col.id] || '-'}</td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                            <small className="text-muted">
                                This is a sample preview. Actual report may contain more data based on your filters and date range.
                            </small>
                        </div>
                    ) : (
                        <div className="text-center py-4">
                            <Spinner animation="border" className="text-primary" />
                            <p className="mt-2">Generating preview...</p>
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setShowPreview(false)}>
                        Close
                    </Button>
                    <Button variant="primary" onClick={handleGenerateReport}>
                        Generate Full Report
                    </Button>
                </Modal.Footer>
            </Modal>
                </div>
            </ServiceStatus>
        </ErrorBoundary>
    );
};

export default ReportBuilder;